import React, { useEffect, useState } from "react";
import { api, fmtUSD, fmtNum } from "../lib/api";
import { useYear } from "../context/YearContext";
import { Sparkles } from "lucide-react";

/** Dashboard tile for overrun bushel revenue — "money that would've been left on the table". */
export default function OverrunEarningsCard() {
  const { year } = useYear();
  const [data, setData] = useState(null);

  useEffect(() => {
    api
      .get(`/reports/overrun-earnings?year=${year}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, [year]);

  if (!data || data.count === 0) return null;

  return (
    <div className="bg-white border-2 border-[#1F2A47] mb-6" data-testid="overrun-earnings-card">
      <div className="bg-[#1F2A47] text-white px-4 py-2 flex items-center gap-2">
        <Sparkles className="w-4 h-4" />
        <span className="text-[11px] font-bold uppercase tracking-[0.2em]">Overrun Earnings · {year}</span>
      </div>
      <div className="p-4">
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Revenue</div>
            <div className="font-display text-2xl font-black tabular text-[#1A4135]" data-testid="overrun-total-revenue">
              {fmtUSD(data.total_revenue)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E]">Bushels</div>
            <div className="font-display text-2xl font-black tabular" data-testid="overrun-total-bushels">
              {fmtNum(data.total_bushels, 0)}
            </div>
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#66645E] whitespace-nowrap" title="Extra dollars earned by selling overrun above the contract price">
              Bonus vs Contract
            </div>
            <div className="font-display text-2xl font-black tabular text-[#BC4727]" data-testid="overrun-bonus">
              {fmtUSD(data.bonus_vs_contract)}
            </div>
          </div>
        </div>

        {data.by_field.length > 0 && (
          <ul className="divide-y divide-[#E5E2D6] -mx-1">
            {data.by_field.slice(0, 4).map((row, i) => (
              <li
                key={row.field_id || i}
                className="flex items-center justify-between px-1 py-1.5"
                data-testid={`overrun-row-${row.field_id || "unalloc"}`}
              >
                <div>
                  <div className="font-semibold text-sm">{row.field_name}</div>
                  <div className="text-[10px] text-[#66645E] uppercase tracking-wider">
                    {row.crop} · {fmtNum(row.bushels, 0)} bu · {row.count} fill{row.count === 1 ? "" : "s"}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold tabular text-[#1A4135]">{fmtUSD(row.revenue)}</div>
                  {row.bonus_vs_contract > 0 && (
                    <div className="text-[10px] text-[#BC4727] font-bold uppercase tracking-wider">
                      +{fmtUSD(row.bonus_vs_contract)} vs contract
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}

        <div className="text-[10px] text-[#66645E] uppercase tracking-wider mt-3 border-t border-[#E5E2D6] pt-2">
          Bushels harvested beyond contract bushels, sold at cash price.
        </div>
      </div>
    </div>
  );
}
